import { useCallback, useEffect, useRef, useState } from 'react';
import { api, session } from '../../shell/bridge';
import { Pressable } from '../../ui/Pressable';
import { SkeletonBlock } from '../../ui/Skeleton';
import { SubTabs, SubPanel, subPanelId, subTabId, type SubTabDef } from '../../components/SubTabs';
import { DatesPrefsPanel } from './DatesPrefs';
import { dateCostLabel, dateEnergyLabel, dateModeLabel } from '../../lib/dateIdeas';
import './dates.css';

/**
 * Us → Dates (Date Planner Phase A, card ZdYBVP203b8a).
 *
 * Four segments behind the top control (PRD §6.2): Plan together (the
 * active pipeline) / Your ideas (the pool) / History (closed dates) /
 * Preferences (card B9pPdlK1CciE — a segment, not a gear beside the row).
 * The suggestion card is the core artifact and is shown to
 * each partner independently — the reveal invariant lives server-side in
 * DatesSlice.toView, so this panel only ever reads `myAnswer` for its own
 * state and `revealed` before it shows the partner's line.
 *
 * Affirm is one tap ("I'm in" — deliberately committal, it writes a calendar
 * event once both are in). Revise is one-tap reason chips, never free text;
 * they map to the constraint set the concierge learns from. Both answer ->
 * the reveal: a LOCKED card, or a new suggestion with a learned line.
 */

interface DateView {
  ideaId: string;
  title: string;
  note: string | null;
  source: string;
  mode: string;
  costBand: string;
  energy: string;
  placeName: string | null;
  placeUrl: string | null;
  placeId: string | null;
  placeAddress: string | null;
  explainer: string | null;
  state: string;
  suggestedAt: number | null;
  lockedAt: number | null;
  completedAt: number | null;
  when: number | null;
  calendarStatus: string;
  vetoReason: string | null;
  rounds: number;
  createdAt: number;
  myAnswer: 'affirm' | 'revise' | null;
  myReason: string | null;
  revealed: boolean;
  answers: { memberId: string; answer: 'affirm' | 'revise'; reason: string | null }[];
  learnedNote: string | null;
}

const REVISE_CHIPS: { value: string; label: string }[] = [
  { value: 'too_expensive', label: 'Too much $' },
  { value: 'too_far', label: 'Too far' },
  { value: 'not_feeling_it', label: 'Not feeling it' },
  { value: 'bad_timing', label: 'Bad timing' },
  { value: 'other', label: 'Other' },
];

/* Card aM7Pzbj2RmeF — the labels come from lib/dateIdeas so every surface
   says "Cozy" the same way. They used to be local, lowercase ('cozy',
   'easy', 'big'), and painted raw beside title-case siblings. */

/* Card U73rzJSR738z (Jon email 2026-10-21): the concierge acknowledges the
   answer in its own voice. Affirm rotates through variants of Jonathan's line
   ("Excellent, duly noted for future plans.") so a repeat tap never reads
   like a stuck record; Revise gets its own fixed wording. One line, in the
   card — never a toast, never stacked on top of the status line. */
const ACK_AFFIRM = [
  'Excellent, duly noted for future plans.',
  'Noted — that goes into the next round of plans.',
  'Duly noted — that is how the next ones get planned.',
];
const ACK_REVISE = 'Understood — that will be taken into account going forward.';

const DATES_TABS: SubTabDef[] = [
  { key: 'plan', label: 'Plan together' },
  { key: 'ideas', label: 'Your ideas' },
  { key: 'history', label: 'History' },
  /* Card B9pPdlK1CciE (Jonathan, email 2026-10-21): "dates the preferences
     should be a tab inline with everything else rather than a separate icon".
     This supersedes the PRD §6.4 / card GMat5z0Zxutf rule that preferences
     stay a surface outside the row — the row now carries four segments. */
  { key: 'prefs', label: 'Preferences' },
];

function metaLine(d: DateView): string {
  const bits: string[] = [];
  if (d.costBand) bits.push(dateCostLabel(d.costBand));
  if (d.mode) bits.push(dateModeLabel(d.mode));
  if (d.energy) bits.push(dateEnergyLabel(d.energy));
  return bits.join(' · ');
}

/** Epoch-ms → the value a datetime-local input expects (local wall time,
 *  minutes precision, no timezone suffix). */
function toLocalInputValue(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** State label the detail rail uses for one idea's stage. */
const STATE_LABEL: Record<string, string> = {
  pool: 'In your ideas',
  suggested: 'On the table',
  locked: "You're both in",
  completed: 'Done',
  raincheck: 'Raincheck',
};

export function DatesPanel({ members, prefsSignal = 0, onPrefsOpened, onAddIdea }: {
  members: { memberId: string; displayName: string }[];
  prefsSignal?: number;
  onPrefsOpened?: () => void;
  /** Card BNw401SBOLFO: Dates no longer authors an idea itself. The one
   *  "+ Idea" door hands the person to the Notebook's date capture (the single
   *  authoring surface); App routes there with the compose intent that opens
   *  the bar on the date chip. */
  onAddIdea?: () => void;
}) {
  const me = session();
  const [ideas, setIdeas] = useState<DateView[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState('plan');
  const [busy, setBusy] = useState(false);
  const [showRevise, setShowRevise] = useState(false);
  /* Card B9pPdlK1CciE: preferences are a tab in the row now, not a gear beside
     it — prefsFrom remembers which tab opened them so Done walks back there. */
  const prefsFrom = useRef('plan');
  const openPrefs = () => setTab(prev => { if (prev !== 'prefs') prefsFrom.current = prev; return 'prefs'; });
  /* Card U73rzJSR738z: the ack rides with the answer it belongs to, so a
     later suggestion never inherits yesterday's line. */
  const [ack, setAck] = useState<{ kind: 'affirm' | 'revise'; ideaId: string; text: string } | null>(null);
  const ackSeq = useRef(0);
  const partner = members.find(m => m.memberId !== me?.memberId);
  const partnerName = partner?.displayName ?? 'your partner';

  /* ── Desktop uplift A3 (card s7njsXdxtTXV) — the three-zone shell ──────
     At the SHIPPED >=1100px three-zone token (the same matchMedia seam
     Thread, A1b and A2 use) Dates becomes preferences left / planner centre /
     selected idea detail right. Below the token none of it exists — `wide` is
     false, so no aside enters the DOM, the segment row keeps its four entries
     and the box tree is the one that shipped. */
  const [wide, setWide] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1100px)').matches);
  /* The right zone's subject: an explicit pick from a list, else the tab's own
     head idea, else (once dismissed) nothing at all. */
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailDismissed, setDetailDismissed] = useState(false);
  const detailOpener = useRef<string | null>(null);
  const detailWantsFocus = useRef(false);
  const prefsRailRef = useRef<HTMLElement | null>(null);
  const detailRailRef = useRef<HTMLElement | null>(null);

  /* When the token is live the Preferences SEGMENT retires — its panel lives
     in the left rail from now on, so the row must only carry panes that
     really exist. A session that arrives on prefs while wide (resize, the
     More-menu quick link) falls back to the tab that opened them instead of
     claiming a pane nobody renders. */
  const tabs = wide ? DATES_TABS.filter(t => t.key !== 'prefs') : DATES_TABS;
  const effTab = wide && tab === 'prefs'
    ? (prefsFrom.current !== 'prefs' ? prefsFrom.current : 'plan')
    : tab;

  const load = useCallback(async () => {
    try {
      const data = await api<{ ideas: DateView[] }>('/api/dates');
      setIdeas(Array.isArray(data.ideas) ? data.ideas : []);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* Card GMat5z0Zxutf: the More menu's "Date preferences" quick link lands us
     on this tab and bumps the signal — open preferences for them, then ACK it.
     Without the ack the signal stays raised for the session and this effect
     fires again on every remount, so simply returning to Dates later throws
     the preferences tab back open (review 2026-10-19).
     Desktop uplift A3: at the three-zone token there is no prefs SEGMENT to
     open — the panel is already standing in the left rail, so the link's job
     becomes "put focus there" instead of swapping the centre pane. */
  useEffect(() => {
    if ((prefsSignal ?? 0) > 0) {
      if (wide) {
        requestAnimationFrame(() => prefsRailRef.current
          ?.querySelector<HTMLElement>('#dates-prefs-cadence, h3, button')
          ?.focus());
      } else {
        openPrefs();
      }
      onPrefsOpened?.();
    }
  }, [prefsSignal, onPrefsOpened, wide]);

  /* The token is a live seam: resizing across it retires / restores the prefs
     segment, and the matching pane must never be left claiming a segment that
     is no longer there (same contract A2 applies to its full-screen detour). */
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1100px)');
    const sync = () => setWide(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  /* Due-check on read (card yejoF8uTVxyf, PRD §7/§9): opening the surface IS
   * the trigger — there is no couples index outside the Durable Objects, so a
   * Worker cron is not an option. The server is idempotent (one nudge per
   * local month, only in a Thursday-evening or Monday slot), so a remount
   * costs one call and can never double-post. The browser's own offset is
   * what makes "Thursday evening" the couple's Thursday evening; a failure is
   * swallowed on purpose — the surface still works, the nudge waits for the
   * next open. */
  useEffect(() => {
    api('/api/dates/nudge', { body: { offsetMinutes: new Date().getTimezoneOffset() } })
      .catch(() => { /* never block the surface over a nudge */ });
  }, []);

  const all = ideas ?? [];
  const suggested = all.find(d => d.state === 'suggested') ?? null;
  const locked = all.find(d => d.state === 'locked') ?? null;
  const pool = all.filter(d => d.state === 'pool');
  const history = all
    .filter(d => d.state === 'completed' || d.state === 'raincheck')
    .sort((a, b) => (b.completedAt ?? b.lockedAt ?? 0) - (a.completedAt ?? a.lockedAt ?? 0));

  /* ── The right zone's subject (A3) ──────────────────────────────────────
     An explicit pick wins; otherwise a head idea the CENTRE pane is not
     already showing keeps the rail additive — saved idea first, then history,
     then the round on the table (only when nothing else exists, so the rail
     never just mirrors the plan card). Dismissing clears the pick AND the
     fallback, so Esc has one visible outcome instead of snapping back. The
     rail itself is a zone, not a modal — it stays mounted. */
  const fallbackDetail = pool[0] ?? history[0] ?? suggested ?? locked ?? null;
  const pickedDetail = detailId ? (all.find(d => d.ideaId === detailId) ?? null) : null;
  const detail = pickedDetail ?? (detailDismissed ? null : fallbackDetail);
  /** Opened by a row/segment: remember the opener so Esc can hand focus back. */
  const pickDetail = (id: string, openerEl: string | null) => {
    detailOpener.current = openerEl;
    detailWantsFocus.current = true;
    setDetailDismissed(false);
    setDetailId(id);
  };
  const dismissDetail = () => {
    setDetailDismissed(true);
    setDetailId(null);
    const back = detailOpener.current;
    detailOpener.current = null;
    const target = back
      ? document.getElementById(back)
      : document.getElementById(`dates-tab-${effTab}`);
    target?.focus();
  };

  /* Explicit opens move focus into the rail (its Close control); first paint
     and the fallback never steal the caret — same contract as A2's rail. */
  useEffect(() => {
    if (!wide || !detailWantsFocus.current) return;
    detailWantsFocus.current = false;
    detailRailRef.current?.querySelector<HTMLElement>('.dates-detail-close, button')?.focus();
  }, [wide, detailId]);

  /* Esc closes the detail the same way the rail's Close does: clears the pick
     and returns focus to whatever opened it. */
  useEffect(() => {
    if (!wide || !detail) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      dismissDetail();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); await load(); } catch { /* the reload shows the truth */ } finally { setBusy(false); setShowRevise(false); }
  };

  const suggest = (generate = false) => run(() => api('/api/dates/suggest', { body: generate ? { generate: true } : {} }));
  const affirm = (id: string) => {
    setAck({ kind: 'affirm', ideaId: id, text: ACK_AFFIRM[ackSeq.current++ % ACK_AFFIRM.length] });
    return run(() => api(`/api/dates/${id}/answer`, { body: { answer: 'affirm' } }));
  };
  /* A Revise closes the round server-side — the card is replaced by the empty
     plan pane — so its ack cannot live on the card that just went away. The
     panel carries it instead (card U73rzJSR738z). */
  const revise = (id: string, reason: string) => {
    setAck({ kind: 'revise', ideaId: id, text: ACK_REVISE });
    return run(() => api(`/api/dates/${id}/answer`, { body: { answer: 'revise', reason } }));
  };
  const promote = (id: string) => run(() => api(`/api/dates/${id}/promote`, { body: {} }));
  /* "Not for us" (card qyhVbyMpcN0B): parks the idea out of every list and
     into the next proposal round's avoid list. The reload is what shows it
     worked — the row simply is not there any more. */
  const dismiss = (id: string) => run(() => api(`/api/dates/${id}/dismiss`, { body: {} }));
  const raincheck = (id: string) => run(() => api(`/api/dates/${id}/outcome`, { body: { outcome: 'raincheck' } }));
  // PRD §11: a locked date with no time cannot be written to a calendar, so
  // the card asks for one. datetime-local sends local wall time; the epoch-ms
  // conversion happens here, once.
  const setWhen = (id: string, value: string) => {
    const ms = new Date(value).getTime();
    if (!Number.isFinite(ms)) return;
    return run(() => api(`/api/dates/${id}/when`, { body: { when: ms } }));
  };

  if (failed) {
    return <p className="dates-empty">Couldn't load your date ideas. Pull to try again.</p>;
  }

  return (
    <div className="dates-root" id="us-dates-panel">
      {/* Heading discipline (Jonathan 2026-10-17): the panel's name is on the
          page, not only on the sub-tab row — same h2 treatment as Questions,
          Requests, Chores, Inbox, Plan and Digest. */}
      <h2 className="dates-title" id="us-dates-title">
        Dates
      </h2>
      <div className="dates-topbar">
        <SubTabs
          name="dates"
          label="Date planner sections"
          tabs={tabs}
          active={effTab}
          /* Card B9pPdlK1CciE: Preferences is one of the segments, so the row
             always names a real pane — no overlay swap, no tab left claiming
             a panel that is not there. At the A3 token that segment is retired
             (the panel stands in the left rail), so `tabs` no longer offers it
             and `effTab` can never name a pane the row does not carry. */
          onChange={(key) => { if (key === 'prefs') prefsFrom.current = effTab; setTab(key); setAck(null); }}
          panelFor={(key) => (key === effTab ? subPanelId('dates', key) : undefined)}
        />
      </div>

      {/* ── Desktop uplift A3 (card s7njsXdxtTXV): three zones at the shipped
          >=1100px token — preferences folded in left, the planner centre, the
          selected idea's detail right. Below the token the wrapper is
          display:contents and both asides are unrendered, so the phone box
          tree is byte-for-byte the one that shipped. DOM order equals visual
          order (left → centre → right), so tab order follows the eye. */}
      <div className={wide ? 'dates-zones is-three' : 'dates-zones'}>
        {wide && (
          <aside
            className="dates-zone-prefs nb-context"
            data-date-prefs-rail
            aria-label="Date preferences"
            tabIndex={-1}
            ref={prefsRailRef}
          >
            <DatesPrefsPanel partnerName={partnerName} onDone={() => {
              /* In the rail there is no pane to leave: Done just hands focus
                 back to the segment row that brought the user here. */
              requestAnimationFrame(() => document.getElementById(`dates-tab-${effTab}`)?.focus());
            }} />
          </aside>
        )}

        <SubPanel name="dates" tabKey={effTab}>
        {effTab === 'prefs' && (
          <DatesPrefsPanel
            partnerName={partnerName}
            onDone={() => {
              /* Done leaves preferences the way it was entered: back to the tab
                 that opened them, focus on that segment (the close button
                 unmounts with this panel, which would otherwise leave focus on
                 <body>). rAF so the focus lands after React has committed. */
              const back = prefsFrom.current;
              setTab(back);
              requestAnimationFrame(() => document.getElementById(subTabId('dates', back))?.focus());
            }}
          />
        )}
        {effTab === 'plan' && (
          <div className="dates-pane" id="dates-plan">
            {/* A Revise's ack: the card it answered is already back in the
                pool, so the pane carries the line (card U73rzJSR738z). */}
            {ack?.kind === 'revise' && (
              <p className="date-card-ack" id="dates-ack" data-testid="date-ack">{ack.text}</p>
            )}
            {locked ? (
              <div className="date-card is-locked" id="dates-locked">
                <div className="date-card-kicker">You're both in</div>
                <div className="date-card-title">{locked.title}</div>
                {locked.placeName && <div className="date-card-place">{locked.placeName}</div>}
                <div className="date-card-meta">{metaLine(locked)}</div>
                {locked.explainer && <div className="date-card-note">{locked.explainer}</div>}
                {locked.when != null && (
                  <div className="date-card-when" data-testid="date-when">
                    {new Date(locked.when).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                  </div>
                )}
                {locked.calendarStatus === 'written' && <div className="date-card-cal">On the calendar</div>}
                {locked.calendarStatus === 'queued' && <div className="date-card-cal">Going on the calendar</div>}
                {locked.calendarStatus === 'needs_time' && (
                  <div className="date-card-cal is-muted" data-testid="date-needs-time">Pick a time and we'll put it on the calendar</div>
                )}
                {locked.calendarStatus === 'failed' && (
                  <div className="date-card-cal is-muted">Couldn't reach the calendar — we'll try again</div>
                )}
                {locked.calendarStatus === 'not_connected' && (
                  <div className="date-card-cal is-muted">Not on the calendar yet — connect Google to auto-add</div>
                )}
                {/* PRD §11: the time is the one thing a calendar write cannot
                    invent. One field, applied on change — no save button to
                    forget. */}
                <label className="date-time-label" htmlFor="dates-when-input">When</label>
                <input
                  id="dates-when-input"
                  className="dates-input date-time-input"
                  type="datetime-local"
                  data-testid="date-when-input"
                  defaultValue={locked.when != null ? toLocalInputValue(locked.when) : ''}
                  onChange={e => e.target.value && void setWhen(locked.ideaId, e.target.value)}
                />
                <div className="date-card-actions">
                  <Pressable className="pear-btn pear-btn-blush" onClick={() => raincheck(locked.ideaId)} disabled={busy}>
                    Raincheck
                  </Pressable>
                </div>
              </div>
            ) : suggested ? (
              <SuggestionCard
                d={suggested}
                busy={busy}
                showRevise={showRevise}
                onToggleRevise={() => setShowRevise(v => !v)}
                onAffirm={() => affirm(suggested.ideaId)}
                onRevise={(reason) => revise(suggested.ideaId, reason)}
                /* Only the ack for THIS idea prefixes the line; a stale one
                   from a parked idea never rides along (card U73rzJSR738z). */
                ackPrefix={ack?.kind === 'affirm' && ack.ideaId === suggested.ideaId ? ack.text : null}
                partnerName={partnerName}
              />
            ) : (
              <div className="dates-plan-empty" id="dates-plan-empty">
                <h3 className="dates-headline">No date on the calendar this month.</h3>
                <p className="dates-sub">Pull the next idea and see if you're both into it.</p>
                <div className="dates-plan-actions">
                  <Pressable className="pear-btn pear-btn-mustard" onClick={() => suggest()} disabled={busy} id="dates-suggest">
                    Give us a date
                  </Pressable>
                  {/* Card gMiWQUTVrPSU: with ideas already saved, the plain
                      button above would just hand back the saved pool — so the
                      plan surface offers a fresh one beside it. Same screen,
                      same round, the pool list below stays the source of truth. */}
                  {pool.length > 0 && (
                    <Pressable
                      className="pear-btn pear-btn-ghost"
                      onClick={() => suggest(true)}
                      disabled={busy}
                      id="dates-generate"
                      data-testid="dates-generate"
                    >
                      Generate a new idea
                    </Pressable>
                  )}
                </div>
              </div>
            )}

            {pool.length > 0 && (
              <button type="button" className="dates-pool-row" onClick={() => setTab('ideas')}>
                {pool.length} idea{pool.length === 1 ? '' : 's'} waiting in Your ideas →
              </button>
            )}
          </div>
        )}

        {effTab === 'ideas' && (
          <div className="dates-pane" id="dates-ideas">
            {/* Card BNw401SBOLFO (IA phase 3): the composer that lived here is
                retired — a date idea is authored in ONE place, the Notebook's
                capture bar, and this surface only plans, answers and reports.
                This door is the only authoring affordance left: it lands on the
                Notebook date capture with the bar focused (App's compose-intent
                route). Any idea saved there is in this list next time you are
                here, with no app reload. */}
            <div className="dates-ideas-head">
              <p className="dates-ideas-hint">A date idea is added in your Notebook — one place for everything you save.</p>
              <Pressable
                className="pear-btn pear-btn-mustard dates-add-btn"
                id="dates-idea-door"
                data-testid="dates-idea-door"
                onClick={() => onAddIdea?.()}
              >
                + Idea
              </Pressable>
            </div>

            {ideas === null ? (
              <SkeletonBlock lines={3} />
            ) : pool.length === 0 ? (
              <p className="dates-empty">No ideas saved yet — tap + Idea to add one in your Notebook, and it lands here ready to send over.</p>
            ) : (
              <ul className="dates-list" id="dates-pool-list">
                {pool.map(d => (
                  <li key={d.ideaId} className="date-row" data-idea={d.ideaId}>
                    {/* Card NAQaaGqRcDwD (Jonathan, email 2026-11-03): the card
                        IS the tap target. Measured at 390px the old flex pair
                        left the text 56px of a 358px row, so the title stacked
                        over five lines and nothing said "this opens". One
                        button carries title + meta + carets; the actions sit on
                        their own line beneath it at phone width. */}
                    <button
                      type="button"
                      className="date-row-open"
                      id={`dates-detail-open-${d.ideaId}`}
                      onClick={() => pickDetail(d.ideaId, `dates-detail-open-${d.ideaId}`)}
                      aria-expanded={detailId === d.ideaId}
                      aria-label={`${d.title} — open this idea`}
                    >
                      <span className="date-row-main">
                        <span className="date-row-title">{d.title}</span>
                        {d.note && <span className="date-row-note">{d.note}</span>}
                        <span className="date-row-meta">{metaLine(d)}</span>
                      </span>
                      <span className="date-row-caret" aria-hidden="true">›</span>
                    </button>
                    <div className="date-row-actions">
                      <Pressable
                        className="pear-btn pear-btn-blush date-row-act"
                        id="dates-promote"
                        onClick={() => promote(d.ideaId)}
                        disabled={busy}
                      >
                        Send to {partnerName}
                      </Pressable>
                      <Pressable
                        className="pear-btn pear-btn-ghost date-row-act"
                        data-testid="date-dismiss"
                        onClick={() => dismiss(d.ideaId)}
                        disabled={busy}
                      >
                        Not for us
                      </Pressable>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {effTab === 'history' && (
          <div className="dates-pane" id="dates-history">
            {ideas === null ? (
              <SkeletonBlock lines={3} />
            ) : history.length === 0 ? (
              <p className="dates-empty">Nothing here yet — locked dates land here once they're done or rained out.</p>
            ) : (
              <ul className="dates-list" id="dates-history-list">
                {history.map(d => (
                  <li key={d.ideaId} className={`date-row is-${d.state}`}>
                    <div className="date-row-main">
                      <div className="date-row-title">{d.title}</div>
                      <div className="date-row-meta">
                        <span className={`date-chip date-chip-${d.state}`}>{d.state === 'completed' ? 'Done' : 'Raincheck'}</span>
                        {metaLine(d)}
                      </div>
                    </div>
                    {wide && (
                      <div className="date-row-actions">
                        <Pressable
                          className="pear-btn pear-btn-ghost date-row-act"
                          id={`dates-detail-open-${d.ideaId}`}
                          onClick={() => pickDetail(d.ideaId, `dates-detail-open-${d.ideaId}`)}
                          aria-pressed={detailId === d.ideaId}
                        >
                          Details
                        </Pressable>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </SubPanel>

        {/* ── Right zone: the selected idea's detail (A3). A zone, not a
            modal — it stays mounted, so Esc/Close clears the pick instead of
            unmounting the rail, and focus goes back to whatever opened it. */}
        {wide && (
          <aside
            className="dates-zone-detail nb-context"
            data-date-detail-rail
            aria-label="Idea detail"
            ref={detailRailRef}
          >
            {detail ? (
              <div className="dates-detail" id="dates-detail">
                <div className="nb-context-top">
                  <span className="dates-detail-kicker" id="dates-detail-kicker">
                    {STATE_LABEL[detail.state] ?? detail.state}
                  </span>
                  <button
                    type="button"
                    className="nb-context-close dates-detail-close"
                    id="dates-detail-close"
                    onClick={dismissDetail}
                  >
                    Close
                  </button>
                </div>
                <DateDetailBody detail={detail} />
              </div>
            ) : (
              <p className="dates-empty" id="dates-detail-empty">
                No idea open — pick one from your list to see it here.
              </p>
            )}
          </aside>
        )}
      </div>

      {/* Card NAQaaGqRcDwD: below the three-zone token there is no rail, so the
          tap a row offers has to land somewhere. Same body, in a sheet, only
          after an explicit pick (never on first paint). */}
      {!wide && pickedDetail && (
        <div
          className="dates-sheet-scrim"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dates-detail-title"
          onClick={dismissDetail}
        >
          <div className="dates-sheet" onClick={e => e.stopPropagation()}>
            <div className="nb-context-top">
              <span className="dates-detail-kicker">
                {STATE_LABEL[pickedDetail.state] ?? pickedDetail.state}
              </span>
              <button
                type="button"
                className="nb-context-close dates-detail-close"
                id="dates-sheet-close"
                onClick={dismissDetail}
              >
                Close
              </button>
            </div>
            <DateDetailBody detail={pickedDetail} />
          </div>
        </div>
      )}

    </div>
  );
}

/** The selected idea's body — one component, two hosts: the >=1100px right
 *  rail and (card NAQaaGqRcDwD) the phone sheet a row opens. Same fields, same
 *  ids, so a phone reader sees exactly what the rail shows. */
function DateDetailBody({ detail }: { detail: DateView }) {
  return (
    <>
      <h3 className="dates-detail-title" id="dates-detail-title">{detail.title}</h3>
      {detail.placeName && (
        <div className="date-card-place" id="dates-detail-place">{detail.placeName}</div>
      )}
      {detail.placeAddress && (
        <div className="date-detail-line" id="dates-detail-address">{detail.placeAddress}</div>
      )}
      <div className="date-card-meta" id="dates-detail-meta">{metaLine(detail)}</div>
      {detail.note && <p className="date-card-note" id="dates-detail-note">{detail.note}</p>}
      {detail.explainer && <p className="date-card-note">{detail.explainer}</p>}
      {detail.learnedNote && <p className="date-card-learned">{detail.learnedNote}</p>}
      {detail.vetoReason && (
        <p className="date-detail-line" id="dates-detail-veto">
          Last change asked for: {(REVISE_CHIPS.find(c => c.value === detail.vetoReason)?.label ?? detail.vetoReason).toLowerCase()}
        </p>
      )}
      {detail.when != null && (
        <p className="date-detail-line" id="dates-detail-when">
          {new Date(detail.when).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
        </p>
      )}
      <p className="date-detail-line is-muted" id="dates-detail-source">
        From {detail.source} · {detail.mode === 'stay_in' ? 'in' : detail.mode === 'go_out' ? 'out' : 'virtual'}
        {' · '}{detail.rounds} round{detail.rounds === 1 ? '' : 's'}
      </p>
    </>
  );
}

function SuggestionCard({
  d, busy, showRevise, onToggleRevise, onAffirm, onRevise, ackPrefix, partnerName,
}: {
  d: DateView;
  busy: boolean;
  showRevise: boolean;
  onToggleRevise: () => void;
  onAffirm: () => void;
  onRevise: (reason: string) => void;
  /** The concierge's one-line ack, already scoped to this idea by the panel. */
  ackPrefix: string | null;
  partnerName: string;
}) {
  const mine = d.myAnswer;
  return (
    <div className={`date-card is-suggested${mine ? ' is-answered' : ''}`} id="dates-suggestion">
      <div className="date-card-kicker">For you two</div>
      <div className="date-card-title">{d.title}</div>
      {d.placeName && <div className="date-card-place">{d.placeName}</div>}
      <div className="date-card-meta">{metaLine(d)}</div>
      {d.note && <div className="date-card-note">{d.note}</div>}
      {d.explainer && <div className="date-card-note">{d.explainer}</div>}
      {d.learnedNote && <div className="date-card-learned">{d.learnedNote}</div>}

      {mine ? (
        <div className="date-card-answered" data-mine={mine}>
          {ackPrefix ? `${ackPrefix} ` : ''}
          {mine === 'affirm'
            ? `You're in — waiting on ${partnerName}.`
            : `You asked for a change${d.myReason ? ` (${(REVISE_CHIPS.find(c => c.value === d.myReason)?.label ?? d.myReason).toLowerCase()})` : ''} — waiting on ${partnerName}.`}
        </div>
      ) : (
        <>
          <div className="date-card-actions">
            <Pressable className="pear-btn pear-btn-mustard" id="dates-affirm" onClick={onAffirm} disabled={busy}>
              ✦ I'm in
            </Pressable>
            <Pressable className="pear-btn pear-btn-ghost" id="dates-revise" onClick={onToggleRevise} disabled={busy}>
              Revise
            </Pressable>
          </div>
          {showRevise && (
            <div className="date-chips" id="dates-revise-chips" role="group" aria-label="What to change">
              {REVISE_CHIPS.map(c => (
                <button
                  key={c.value}
                  type="button"
                  className="date-chip-opt pear-tap-capsule"
                  data-reason={c.value}
                  onClick={() => onRevise(c.value)}
                  disabled={busy}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
